/**
 * APCard Component
 *
 * A comprehensive card component following Figma design system specifications.
 * Supports multiple types, states, actions, and content slots with accessibility features.
 * Now includes slot-based architecture for maximum flexibility and APIconAstropay integration.
 *
 * @example
 * ```tsx
 * // Basic usage with props
 * <APCard
 *   type="default"
 *   title="Card Title"
 *   titleIcon="info_line"
 *   supportText="Support text"
 *   primaryAction={{
 *     label: "1st Action",
 *     icon: "arrow_right_line",
 *     onClick: handlePrimaryClick
 *   }}
 * >
 *   <div>Card content</div>
 * </APCard>
 *
 * // Using slots for custom content
 * <APCard
 *   type="positive"
 *   slots={{
 *     header: <CustomHeader />,
 *     content: <CustomContent />,
 *     actions: <CustomActions />
 *   }}
 * />
 *
 * // Mixed approach - slots + props
 * <APCard
 *   type="highlighted"
 *   title="Revenue Card"
 *   trend={{
 *     type: "positive",
 *     value: 12.5,
 *     unit: "%"
 *   }}
 *   slots={{
 *     content: <Typography variant="h2">$1,234.56 USD</Typography>
 *   }}
 * />
 * ```
 */

import { forwardRef } from 'react';

import type { IAPCardProps, ICardAction, ICardSlots } from './types';

import './styles.scss';

import APButton from '@shared/components/atoms/button';
import APIconAstropay from '@shared/components/atoms/media/icon-ap';

import type { TAPIconAstropayType } from '@shared/components/atoms/media/icon-ap/mingcute.alias';

import CardSkeleton from './components/card-skeleton';

/**
 * APCard - Production-ready card component
 *
 * Follows AstroPay design system and accessibility guidelines.
 */
/**
 * Helper function to render icons - supports both ReactNode and APIconAstropay icon names
 */
const renderIcon = (
  icon?: React.ReactNode | TAPIconAstropayType | string,
  size = 16,
  className = ''
) => {
  if (!icon) return null;

  if (typeof icon === 'string') {
    return <APIconAstropay icon={icon as TAPIconAstropayType} size={size} className={className} />;
  }
  return icon;
};

export const APCard = forwardRef<HTMLDivElement, IAPCardProps>(
  (
    {
      type = 'default',
      size = 'medium',
      state = 'default',
      title,
      titleIcon,
      children,
      supportText,
      actions,
      slots = {},
      actionsPosition = 'bottom',
      className = '',
      clickable = false,
      onCardClick,
      'data-testid': testId,
      ...rest
    },
    ref
  ) => {
    // Helper function to convert legacy actions to new actions array format
    const getAllActions = (): ICardAction[] => {
      // If new actions array is provided, use it
      if (actions && actions.length > 0) {
        return actions;
      }
      return [];
    };

    const allActions = getAllActions();
    const hasActions = allActions.length > 0;

    // Show skeleton state
    if (state === 'skeleton') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { onClick, ...skeletonRest } = rest;
      return (
        <CardSkeleton
          ref={ref}
          className={className}
          data-testid={testId}
          showHeader={Boolean(title || slots.header || slots.title)}
          showActions={Boolean(hasActions || slots.actions)}
          onClick={clickable ? onCardClick : undefined}
          {...skeletonRest}
        />
      );
    }

    // Construct class names using BEM methodology
    const baseClasses = 'ap-card';
    const typeClasses = `ap-card--${type}`;
    const sizeClasses = `ap-card--${size}`;
    const stateClasses = [
      clickable && 'ap-card--clickable',
      hasActions && 'ap-card--with-actions',
    ].filter(Boolean);

    const cardClasses = [baseClasses, typeClasses, sizeClasses, ...stateClasses, className]
      .filter(Boolean)
      .join(' ');

    // Determine content using slots or props
    const headerContent = slots.header;
    const titleContent = slots.title;
    const mainContent = slots.content || children;
    const supportContent = slots.supportText;
    const actionsContent = slots.actions;

    const hasHeader = headerContent || titleContent || title || titleIcon;

    const hasFooterActions = actionsContent || hasActions;

    const renderActions = (placement: 'top' | 'bottom' = actionsPosition) => {
      if (!hasFooterActions) return null;

      return (
        <div className={`ap-card__actions ap-card__actions--${placement}`}>
          {actionsContent ? (
            actionsContent
          ) : (
            <>
              {allActions.map((actionItem, index) => (
                <APButton
                  key={index}
                  size="small"
                  variant={actionItem.variant || 'primary'}
                  onClick={actionItem.action}
                  disabled={actionItem.disabled}
                  rightIcon={
                    actionItem.iconPosition !== 'left' && actionItem.icon
                      ? renderIcon(actionItem.icon, 16)
                      : undefined
                  }
                  leftIcon={
                    actionItem.iconPosition === 'left' && actionItem.icon
                      ? renderIcon(actionItem.icon, 16)
                      : undefined
                  }
                  className={`ap-card__action ap-card__action--${actionItem.variant || 'primary'}`}
                >
                  {actionItem.text}
                </APButton>
              ))}
            </>
          )}
        </div>
      );
    };

    return (
      <div
        ref={ref}
        className={cardClasses}
        data-testid={testId}
        onClick={clickable ? onCardClick : undefined}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onCardClick?.();
                }
              }
            : undefined
        }
        {...rest}
      >
        {/* Card Header - Use slot if provided, otherwise default structure */}
        {hasHeader &&
          (headerContent ? (
            <div className="ap-card__header">
              <div className="ap-card__header-left">{headerContent}</div>
              {actionsPosition === 'top' && renderActions('top')}
            </div>
          ) : (
            <div className="ap-card__header">
              <div className="ap-card__header-left">
                {/* Title slot or default title */}
                {titleContent
                  ? titleContent
                  : title && (
                      <div className="ap-card__title-row">
                        <span className="ap-card__title">{title}</span>
                        {titleIcon && (
                          <span className="ap-card__title-icon">{renderIcon(titleIcon, 12)}</span>
                        )}
                      </div>
                    )}
              </div>
              {actionsPosition === 'top' && hasFooterActions && (
                <div className="ap-card__header-right">{renderActions('top')}</div>
              )}
            </div>
          ))}

        {/* Card Content - Use slot if provided, otherwise children */}
        <div className="ap-card__content">{mainContent}</div>

        {/* Support Text - Use slot if provided, otherwise supportText */}
        {(supportContent || supportText) && (
          <div className="ap-card__support">
            {supportContent ? (
              supportContent
            ) : (
              <span className="ap-card__support-text">{supportText}</span>
            )}
          </div>
        )}

        {/* Footer Actions - Use slot if provided, otherwise default actions */}
        {actionsPosition === 'bottom' && renderActions('bottom')}
      </div>
    );
  }
);

APCard.displayName = 'APCard';

export type { IAPCardProps, ICardSlots, ICardAction };
export default APCard;
